/**
 * The bell in the member chrome.
 *
 * At desk width it opens the latest notices over the page, so a member on
 * the board reads them without leaving it; on a phone it stays a link to the
 * river, which is one tap away and a popover over a phone board would be a
 * second scroll box. A notice that lands while a page stands open shows as a
 * toast within the poll interval.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROUTES, toPersianDigits } from '@metal/shared'
import { Button, Empty, IconButton, Segmented, Skeleton, useToast } from '@metal/ui'
import { api } from '@metal/ui/api'
import { useSession } from '@metal/ui/session'
import { qk } from '@/lib/queries'
import { NotificationRow, type NotificationsResponse } from '@/screens/account/NotificationRow'

interface SummaryWithLatest {
  unread: number
  latest: { id: string; titleFa: string; route: string | null; at: string } | null
}

const SUMMARY_KEY = qk.notifications({ view: 'summary' })

/** The summary the bell, the rail and the toast all read, polled every minute. */
function useSummary() {
  const { user } = useSession()
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: () => api.get<SummaryWithLatest>('/notifications/summary'),
    enabled: Boolean(user),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Mounted once in the member layout. A newer unread notice than the one last
 * seen, with the count risen, is toasted; never on the first read of a
 * session, and never on the river, which already shows it.
 */
export function useNotificationArrivals(): void {
  const summary = useSummary()
  const toast = useToast()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const client = useQueryClient()
  const seen = useRef<{ id: string | null; unread: number } | null>(null)

  useEffect(() => {
    const data = summary.data
    if (!data) return
    const latestId = data.latest?.id ?? null
    const before = seen.current
    seen.current = { id: latestId, unread: data.unread }
    if (!before || !data.latest || latestId === before.id || data.unread <= before.unread) return
    if (pathname.startsWith(ROUTES.notifications())) return
    const { id, titleFa, route } = data.latest
    toast.push({
      icon: 'bell',
      message: titleFa,
      timeout: 8000,
      action: route
        ? {
            label: 'مشاهده',
            onClick: () => {
              void api.post(`/notifications/${id}/read`).finally(() => {
                void client.invalidateQueries({ queryKey: ['notifications'] })
              })
              navigate(route)
            },
          }
        : undefined,
    })
  }, [summary.data, pathname, toast, navigate, client])
}

export function NotificationBell({ popover, size = 'sm' }: { popover: boolean; size?: 'sm' | 'md' }) {
  const summary = useSummary()
  const { user } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)

  const unread = summary.isError ? undefined : (summary.data?.unread ?? user?.unreadNotifications)
  const label = unread && unread > 0 ? `${toPersianDigits(unread)} اعلان خوانده نشده` : 'اعلان‌ها'

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      button.current?.focus()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!popover) {
    return <IconButton icon="bell" label={label} size={size} count={unread} to={ROUTES.notifications()} />
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <IconButton
        ref={button}
        icon="bell"
        label={label}
        size={size}
        count={unread}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      {open && <BellPanel unread={unread ?? 0} onClose={() => setOpen(false)} />}
    </div>
  )
}

function BellPanel({ unread, onClose }: { unread: number; onClose: () => void }) {
  const client = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const list = useQuery({
    queryKey: qk.notifications({ view: 'bell', filter }),
    queryFn: () => api.get<NotificationsResponse>('/notifications', { pageSize: 6, filter }),
    staleTime: 0,
    placeholderData: (previous) => previous,
  })

  const refresh = () => void client.invalidateQueries({ queryKey: ['notifications'] })
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSettled: refresh,
  })
  const readOne = (id: string) => {
    void api.post(`/notifications/${id}/read`).finally(refresh)
  }

  return (
    <div
      role="dialog"
      aria-label="اعلان‌ها"
      className="card card-float"
      style={{
        position: 'absolute',
        insetBlockStart: 'calc(100% + 8px)',
        insetInlineEnd: 0,
        inlineSize: 380,
        zIndex: 60,
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', padding: 'var(--s-3)', paddingBlockEnd: 'var(--s-2)' }}>
        <span className="t-sm t-sb">اعلان‌ها</span>
        <Segmented
          value={filter}
          onChange={(v) => setFilter(v === 'unread' ? 'unread' : 'all')}
          options={[
            { value: 'all', label: 'همه' },
            { value: 'unread', label: unread > 0 ? `خوانده نشده ${toPersianDigits(unread)}` : 'خوانده نشده' },
          ]}
        />
        <Button
          size="sm"
          variant="quiet"
          icon="check"
          disabled={unread === 0}
          loading={readAll.isPending}
          onClick={() => readAll.mutate()}
        >
          خواندن همه
        </Button>
      </div>
      <div style={{ borderBlockStart: '1px solid var(--c-border)' }}>
        {list.isPending ? (
          <div className="col" style={{ gap: 8, padding: 'var(--s-3)' }}>
            <Skeleton h={48} />
            <Skeleton h={48} />
            <Skeleton h={48} />
          </div>
        ) : list.isError ? (
          <div style={{ padding: 'var(--s-3)' }}>
            <Empty
              icon="alert"
              title="اعلان‌ها بارگذاری نشد."
              action={
                <Button size="sm" variant="secondary" icon="refresh" onClick={() => void list.refetch()}>
                  تلاش دوباره
                </Button>
              }
            />
          </div>
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <div style={{ padding: 'var(--s-3)' }}>
            <Empty icon="bell" title={filter === 'unread' ? 'اعلان خوانده نشده‌ای ندارید.' : 'هنوز اعلانی ندارید.'} />
          </div>
        ) : (
          list.data!.items.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              compact
              onMarkRead={() => {
                if (!item.read) readOne(item.id)
                onClose()
              }}
            />
          ))
        )}
      </div>
      <div className="row" style={{ justifyContent: 'center', padding: 'var(--s-2)' }}>
        <Button variant="ghost" size="sm" iconEnd="chevron-left" to={ROUTES.notifications()} onClick={onClose}>
          همه اعلان‌ها
        </Button>
      </div>
    </div>
  )
}
